import { Body, Camera } from '../types.js';

// ─── Starfield ───

function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return s / 2147483647;
  };
}

interface Star {
  x: number;
  y: number;
  brightness: number;
  size: number;
}

let stars: Star[] | null = null;
let lastCanvasSize = { w: 0, h: 0 };

function generateStars(w: number, h: number): Star[] {
  const rng = seededRandom(42);
  const count = Math.floor((w * h) / 800);
  const result: Star[] = [];
  for (let i = 0; i < count; i++) {
    result.push({
      x: rng() * w,
      y: rng() * h,
      brightness: 0.2 + rng() * 0.8,
      size: rng() < 0.9 ? 1 : 2,
    });
  }
  return result;
}

function ensureStars(w: number, h: number) {
  if (!stars || w !== lastCanvasSize.w || h !== lastCanvasSize.h) {
    stars = generateStars(w, h);
    lastCanvasSize = { w, h };
  }
}

function drawBackgroundStars(ctx: CanvasRenderingContext2D, camera: Camera, w: number, h: number) {
  if (!stars) return;

  // Parallax offset
  const offsetX = (-camera.centerX * camera.zoom * 0.05) % w;
  const offsetY = (-camera.centerY * camera.zoom * 0.05) % h;

  for (const star of stars) {
    const sx = ((star.x + offsetX) % w + w) % w;
    const sy = ((star.y + offsetY) % h + h) % h;

    ctx.fillStyle = `rgba(255, 255, 255, ${star.brightness * 0.6})`;
    ctx.beginPath();
    ctx.arc(sx, sy, star.size, 0, Math.PI * 2);
    ctx.fill();
  }
}

// ─── Body rendering ───

function drawBody(ctx: CanvasRenderingContext2D, body: Body, camera: Camera, canvasW: number, canvasH: number) {
  const screen = {
    x: (body.x - camera.centerX) * camera.zoom + canvasW / 2,
    y: (body.y - camera.centerY) * camera.zoom + canvasH / 2,
  };
  const r = Math.max(2, body.radius * camera.zoom);

  if (body.isStar) {
    // Star: draw glow layers
    for (let i = 5; i >= 1; i--) {
      const grad = ctx.createRadialGradient(screen.x, screen.y, r * 0.5, screen.x, screen.y, r * (1 + i * 1.5));
      grad.addColorStop(0, body.glowColor);
      grad.addColorStop(1, 'transparent');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(screen.x, screen.y, r * (1 + i * 1.5), 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Body circle with gradient
  const grad = ctx.createRadialGradient(
    screen.x - r * 0.3, screen.y - r * 0.3, r * 0.1,
    screen.x, screen.y, r
  );

  if (body.isStar) {
    grad.addColorStop(0, '#ffffff');
    grad.addColorStop(0.3, body.color);
    grad.addColorStop(1, body.glowColor);
  } else {
    // Planet surface with shading
    const lighter = lightenColor(body.color, 40);
    const darker = darkenColor(body.color, 40);
    grad.addColorStop(0, lighter);
    grad.addColorStop(0.6, body.color);
    grad.addColorStop(1, darker);
  }

  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(screen.x, screen.y, r, 0, Math.PI * 2);
  ctx.fill();

  // Specular highlight
  if (!body.isStar) {
    const specGrad = ctx.createRadialGradient(
      screen.x - r * 0.35, screen.y - r * 0.35, 0,
      screen.x - r * 0.35, screen.y - r * 0.35, r * 0.5
    );
    specGrad.addColorStop(0, 'rgba(255,255,255,0.35)');
    specGrad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = specGrad;
    ctx.beginPath();
    ctx.arc(screen.x - r * 0.35, screen.y - r * 0.35, r * 0.5, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawTrail(ctx: CanvasRenderingContext2D, body: Body, camera: Camera, canvasW: number, canvasH: number) {
  if (body.trail.length < 2) return;

  for (let i = 1; i < body.trail.length; i++) {
    const alpha = (i / body.trail.length) * 0.6;
    const p1 = body.trail[i - 1];
    const p2 = body.trail[i];

    const s1x = (p1.x - camera.centerX) * camera.zoom + canvasW / 2;
    const s1y = (p1.y - camera.centerY) * camera.zoom + canvasH / 2;
    const s2x = (p2.x - camera.centerX) * camera.zoom + canvasW / 2;
    const s2y = (p2.y - camera.centerY) * camera.zoom + canvasH / 2;

    ctx.strokeStyle = body.color + Math.floor(alpha * 255).toString(16).padStart(2, '0');
    ctx.lineWidth = Math.max(1, body.radius * camera.zoom * 0.15);
    ctx.beginPath();
    ctx.moveTo(s1x, s1y);
    ctx.lineTo(s2x, s2y);
    ctx.stroke();
  }
}

// ─── Color helpers ───

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
    : { r: 128, g: 128, b: 128 };
}

function lightenColor(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgb(${Math.min(255, r + amount)}, ${Math.min(255, g + amount)}, ${Math.min(255, b + amount)})`;
}

function darkenColor(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgb(${Math.max(0, r - amount)}, ${Math.max(0, g - amount)}, ${Math.max(0, b - amount)})`;
}

// ─── Public API ───

export class Renderer {
  public ctx: CanvasRenderingContext2D;
  public canvas: HTMLCanvasElement;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
  }

  resize() {
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = window.innerWidth * dpr;
    this.canvas.height = window.innerHeight * dpr;
    this.ctx.scale(dpr, dpr);
    stars = null; // Regenerate starfield for new size
  }

  clear() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.ctx.fillStyle = '#05050f';
    this.ctx.fillRect(0, 0, w, h);

    // Subtle nebula background
    const nebGrad = this.ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) * 0.7);
    nebGrad.addColorStop(0, 'rgba(30, 15, 60, 0.3)');
    nebGrad.addColorStop(0.5, 'rgba(10, 5, 30, 0.15)');
    nebGrad.addColorStop(1, 'rgba(5, 5, 15, 0)');
    this.ctx.fillStyle = nebGrad;
    this.ctx.fillRect(0, 0, w, h);

    ensureStars(w, h);
  }

  drawBackground(camera: Camera) {
    drawBackgroundStars(this.ctx, camera, window.innerWidth, window.innerHeight);
  }

  drawTrails(bodies: Body[], camera: Camera) {
    for (const body of bodies) {
      drawTrail(this.ctx, body, camera, window.innerWidth, window.innerHeight);
    }
  }

  drawBodies(bodies: Body[], camera: Camera) {
    for (const body of bodies) {
      drawBody(this.ctx, body, camera, window.innerWidth, window.innerHeight);
    }
  }

  drawBody(body: Body, camera: Camera) {
    drawBody(this.ctx, body, camera, window.innerWidth, window.innerHeight);
  }

  drawCrosshair(x: number, y: number, camera: Camera) {
    const world = {
      x: (x - window.innerWidth / 2) / camera.zoom + camera.centerX,
      y: (y - window.innerHeight / 2) / camera.zoom + camera.centerY,
    };
    const screen = {
      x: (world.x - camera.centerX) * camera.zoom + window.innerWidth / 2,
      y: (world.y - camera.centerY) * camera.zoom + window.innerHeight / 2,
    };

    this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
    this.ctx.lineWidth = 1;
    this.ctx.setLineDash([4, 4]);
    const size = 10 * camera.zoom;
    this.ctx.beginPath();
    this.ctx.moveTo(screen.x - size, screen.y);
    this.ctx.lineTo(screen.x + size, screen.y);
    this.ctx.moveTo(screen.x, screen.y - size);
    this.ctx.lineTo(screen.x, screen.y + size);
    this.ctx.stroke();
    this.ctx.setLineDash([]);
  }
}
